// Self-contained Code-128 (Code B) barcode → inline SVG. No external dependencies.
// Public-domain algorithm; pattern table is the standard Code 128 width table.
import React from "react";

// 107 symbol patterns (index 0..106) + stop pattern. Each string is the
// sequence of bar/space module widths, starting with a bar.
const PATTERNS = [
    "212222","222122","222221","121223","121322","131222","122213","122312","132212","221213",
    "221312","231212","112232","122132","122231","113222","123122","123221","223211","221132",
    "221231","213212","223112","312131","311222","321122","321221","312212","322112","322211",
    "212123","212321","232121","111323","131123","131321","112313","132113","132311","211313",
    "231113","231311","112133","112331","132131","113123","113321","133121","313121","211331",
    "231131","213113","213311","213131","311123","311321","331121","312113","312311","332111",
    "314111","221411","431111","111224","111422","121124","121421","141122","141221","112214",
    "112412","122114","122411","142112","142211","241211","221114","413111","241112","134111",
    "111242","121142","121241","114212","124112","124211","411212","421112","421211","212141",
    "214121","412121","111143","111341","131141","114113","114311","411113","411311","113141",
    "114131","311141","411131","211412","211214","211232",
];
const START_B = 104;
const STOP = "2331112"; // 13-module stop pattern

// Build the module-width run list for `value` (ASCII 32..126 supported).
function buildRuns(value) {
    const chars = String(value);
    let checksum = START_B;
    const codes = [START_B];
    for (let i = 0; i < chars.length; i++) {
        let code = chars.charCodeAt(i) - 32;
        if (code < 0 || code > 94) code = 0; // fall back to space for unsupported
        codes.push(code);
        checksum += code * (i + 1);
    }
    codes.push(checksum % 103); // checksum symbol
    let runs = "";
    codes.forEach((c) => { runs += PATTERNS[c]; });
    runs += STOP;
    return runs;
}

/**
 * Render a Code-128 barcode as an inline SVG React element.
 * @param {string} value        text to encode (barcode number)
 * @param {number} height       bar height in px
 * @param {number} moduleWidth  width of one module in px
 */
export function Barcode128({ value, height = 50, moduleWidth = 1.6 }) {
    if (!value) return null;
    const runs = buildRuns(value);
    const quiet = 10; // quiet zone modules on each side
    let x = quiet;
    let bar = true; // runs always start with a bar
    const rects = [];
    for (let i = 0; i < runs.length; i++) {
        const w = parseInt(runs[i], 10);
        if (bar) {
            rects.push(<rect key={i} x={x * moduleWidth} y={0} width={w * moduleWidth} height={height} fill="#000" />);
        }
        x += w;
        bar = !bar;
    }
    const totalWidth = (x + quiet) * moduleWidth;
    return (
        <svg
            width={totalWidth}
            height={height}
            viewBox={`0 0 ${totalWidth} ${height}`}
            shapeRendering="crispEdges"
            style={{ display: "block", margin: "0 auto" }}
        >
            <rect x={0} y={0} width={totalWidth} height={height} fill="#fff" />
            {rects}
        </svg>
    );
}
